import authService from './authService'
const { apiClient } = authService

/**
 * Custom error class for Admin Service operations
 * Preserves stack trace and adds service-specific properties
 */
class AdminServiceError extends Error {
  constructor(message, status = null) {
    super(message)
    this.name = 'AdminServiceError'
    this.status = status
    // Capturar stack trace (excluye constructor del stack)
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, AdminServiceError)
    }
  }
}

/**
 * Admin Service para Panel de Administración
 * Maneja comunicación con endpoints admin del backend
 *
 * Aplicando Service Layer pattern de Basket Base
 * Integra con Cases API (7 endpoints) ya implementada con RBAC
 * Sigue auth patterns establecidos con apiClient y CSRF protection
 * Conecta con adminMiddleware backend que requiere role ADMIN
 */

class AdminService {
  /**
   * Obtener estadísticas del dashboard admin
   * Endpoint: GET /api/admin/stats
   */
  async getStats() {
    try {
      const response = await apiClient.get('/admin/stats')
      return {
        success: true,
        data: response.data.data
      }
    } catch (error) {
      console.error('Admin stats error:', error)
      throw new AdminServiceError(
        error.response?.data?.error || 'Error al obtener estadísticas',
        error.response?.status
      )
    }
  }

  /**
   * Obtener casos con filtros y paginación
   * Endpoint: GET /api/admin/cases
   */
  async getCases(params = {}) {
    try {
      const response = await apiClient.get('/admin/cases', { params })
      return {
        success: true,
        data: response.data.data
      }
    } catch (error) {
      console.error('Admin cases error:', error)
      throw new AdminServiceError(
        error.response?.data?.error || 'Error al obtener casos',
        error.response?.status
      )
    }
  }

  /**
   * Actualizar estado de un caso
   * Endpoint: PATCH /api/admin/cases/:id/status
   */
  async updateCaseStatus(caseId, statusData) {
    try {
      const response = await apiClient.patch(
        `/admin/cases/${caseId}/status`,
        statusData
      )
      return {
        success: true,
        data: response.data.data,
        message: response.data.message
      }
    } catch (error) {
      console.error('Update case status error:', error)
      throw new AdminServiceError(
        error.response?.data?.error || 'Error al actualizar estado del caso',
        error.response?.status
      )
    }
  }

  /**
   * Convert File to base64 string
   * @param {File} file - Browser File object
   * @returns {Promise<string>} Base64 encoded string (without data URI prefix)
   */
  _fileToBase64(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.readAsDataURL(file)
      reader.onload = () => {
        // Remove data:mime;base64, prefix
        const base64 = reader.result.split(',')[1]
        resolve(base64)
      }
      reader.onerror = error => reject(error)
    })
  }

  /**
   * Subir documento a un caso (admin upload manual)
   * Endpoint: POST /api/admin/cases/:id/documents
   * Backend expects JSON with base64-encoded file
   */
  async uploadDocument(caseId, file, documentType) {
    try {
      // Convert file to base64 for backend
      const base64 = await this._fileToBase64(file)

      const response = await apiClient.post(
        `/admin/cases/${caseId}/documents`,
        {
          file: base64,
          filename: file.name,
          mimeType: file.type,
          type: documentType,
          notifyUser: true
        }
      )

      return {
        success: true,
        data: response.data.data,
        message: response.data.message
      }
    } catch (error) {
      console.error('Document upload error:', error)
      throw new AdminServiceError(
        error.response?.data?.error || 'Error al subir documento',
        error.response?.status
      )
    }
  }

  /**
   * Obtener lista de usuarios
   * Endpoint: GET /api/admin/users
   */
  async getUsers(params = {}) {
    try {
      const response = await apiClient.get('/admin/users', { params })
      return {
        success: true,
        data: response.data.data
      }
    } catch (error) {
      console.error('Admin users error:', error)
      throw new AdminServiceError(
        error.response?.data?.error || 'Error al obtener usuarios',
        error.response?.status
      )
    }
  }

  /**
   * Obtener detalles de un usuario específico
   * Endpoint: GET /api/admin/users/:id
   */
  async getUserDetails(userId) {
    try {
      const response = await apiClient.get(`/admin/users/${userId}`)
      return {
        success: true,
        data: response.data.data
      }
    } catch (error) {
      console.error('User details error:', error)
      throw new AdminServiceError(
        error.response?.data?.error || 'Error al obtener detalles del usuario',
        error.response?.status
      )
    }
  }

  /**
   * Obtener casos de un usuario específico
   * Endpoint: GET /api/admin/users/:id/cases
   */
  async getUserCases(userId) {
    try {
      const response = await apiClient.get(`/admin/users/${userId}/cases`)
      return {
        success: true,
        data: response.data.data
      }
    } catch (error) {
      console.error('User cases error:', error)
      throw new AdminServiceError(
        error.response?.data?.error || 'Error al obtener casos del usuario',
        error.response?.status
      )
    }
  }

  /**
   * Enviar notificación por email (admin triggered)
   * Endpoint: POST /api/admin/notifications/email
   */
  async sendEmailNotification(data) {
    try {
      const response = await apiClient.post('/admin/notifications/email', data)
      return {
        success: true,
        data: response.data.data,
        message: response.data.message
      }
    } catch (error) {
      console.error('Email notification error:', error)
      throw new AdminServiceError(
        error.response?.data?.error || 'Error al enviar notificación',
        error.response?.status
      )
    }
  }

  /**
   * Enviar email personalizado a un usuario vinculado a un caso
   * Endpoint: POST /api/admin/cases/:id/send-email
   */
  async sendCaseEmail(caseId, { subject, message }) {
    try {
      const response = await apiClient.post(`/admin/cases/${caseId}/send-email`, {
        subject,
        message
      })
      return {
        success: true,
        data: response.data.data,
        message: response.data.message
      }
    } catch (error) {
      console.error('Send case email error:', error)
      throw new AdminServiceError(
        error.response?.data?.error || 'Error al enviar email',
        error.response?.status
      )
    }
  }

  /**
   * Enviar email personalizado a un usuario
   * Endpoint: POST /api/admin/users/:id/send-email
   */
  async sendUserEmail(userId, { subject, message }) {
    try {
      const response = await apiClient.post(`/admin/users/${userId}/send-email`, {
        subject,
        message
      })
      return {
        success: true,
        data: response.data.data,
        message: response.data.message
      }
    } catch (error) {
      console.error('Send user email error:', error)
      throw new AdminServiceError(
        error.response?.data?.error || 'Error al enviar email',
        error.response?.status
      )
    }
  }

  /**
   * Obtener actividad reciente del sistema
   * Endpoint: GET /api/admin/activity
   */
  async getRecentActivity(limit = 20) {
    try {
      const response = await apiClient.get('/admin/activity', {
        params: { limit }
      })
      return {
        success: true,
        data: response.data.data
      }
    } catch (error) {
      console.error('Recent activity error:', error)
      throw new AdminServiceError(
        error.response?.data?.error || 'Error al obtener actividad reciente',
        error.response?.status
      )
    }
  }

  /**
   * Obtener documentos de un caso
   * Endpoint: GET /api/admin/cases/:id/documents
   * Sprint 3: Admin Documents Panel
   */
  async getCaseDocuments(caseId) {
    try {
      const response = await apiClient.get(`/admin/cases/${caseId}/documents`)
      return {
        success: true,
        data: response.data.data
      }
    } catch (error) {
      console.error('Get case documents error:', error)
      throw new AdminServiceError(
        error.response?.data?.error || 'Error al obtener documentos del caso',
        error.response?.status
      )
    }
  }

  /**
   * Verificar un documento
   * Endpoint: PUT /api/admin/documents/:id/verify
   * Sprint 3: Admin Documents Panel
   */
  async verifyDocument(documentId, data = {}) {
    try {
      const response = await apiClient.put(`/admin/documents/${documentId}/verify`, data)
      return {
        success: true,
        data: response.data.data,
        message: response.data.message
      }
    } catch (error) {
      console.error('Verify document error:', error)
      throw new AdminServiceError(
        error.response?.data?.error || 'Error al verificar documento',
        error.response?.status
      )
    }
  }

  /**
   * Rechazar un documento con motivo
   * Endpoint: PUT /api/admin/documents/:id/reject
   * Sprint 3: Admin Documents Panel
   * @param {string} documentId - ID del documento
   * @param {object} data - { reason: string } - motivo requerido, min 10 chars, max 500
   */
  async rejectDocument(documentId, data) {
    try {
      const response = await apiClient.put(`/admin/documents/${documentId}/reject`, data)
      return {
        success: true,
        data: response.data.data,
        message: response.data.message
      }
    } catch (error) {
      console.error('Reject document error:', error)
      throw new AdminServiceError(
        error.response?.data?.error || 'Error al rechazar documento',
        error.response?.status
      )
    }
  }

  /**
   * Eliminar un documento (admin only)
   * Endpoint: DELETE /api/admin/documents/:id
   * Sprint 3: Admin Documents Panel
   * @param {string} documentId - ID del documento
   */
  async deleteDocument(documentId) {
    try {
      const response = await apiClient.delete(`/admin/documents/${documentId}`)
      return {
        success: true,
        data: response.data.data,
        message: response.data.message
      }
    } catch (error) {
      console.error('Delete document error:', error)
      throw new AdminServiceError(
        error.response?.data?.error || 'Error al eliminar documento',
        error.response?.status
      )
    }
  }

  /**
   * Get all payments with filters (admin only)
   * Sprint 03: Frontend Servicios - Pagos
   * Endpoint: GET /api/admin/payments
   * @param {Object} params - Query parameters
   * @param {number} params.page - Page number
   * @param {number} params.limit - Items per page
   * @param {string} params.status - Filter by status
   * @param {string} params.tier - Filter by tier
   * @param {string} params.startDate - Filter by start date (ISO)
   * @param {string} params.endDate - Filter by end date (ISO)
   * @param {string} params.sortBy - Sort field
   * @param {string} params.sortOrder - Sort order (asc/desc)
   * @returns {Promise<Object>} Payments list with pagination
   */
  async getPayments(params = {}) {
    try {
      const queryParams = new URLSearchParams()

      // Add non-null params
      Object.entries(params).forEach(([key, value]) => {
        if (value !== null && value !== undefined && value !== '') {
          queryParams.append(key, value.toString())
        }
      })

      const response = await apiClient.get(`/admin/payments?${queryParams}`)

      return {
        success: true,
        payments: response.data.data.payments,
        pagination: response.data.data.pagination
      }
    } catch (error) {
      console.error('Error fetching admin payments:', error)
      throw new AdminServiceError(
        error.response?.data?.error || 'Error al obtener los pagos',
        error.response?.status
      )
    }
  }

  /**
   * Actualizar estado de una URL de un caso
   * Endpoint: PATCH /api/admin/cases/:id/urls/:urlId
   * @param {string} caseId - ID del caso
   * @param {string} urlId - ID de la URL
   * @param {Object} data - { status, notes }
   * @param {string} data.status - Nuevo estado (pending, contacted, resolved)
   * @param {string} [data.notes] - Notas opcionales (max 500 chars)
   * @returns {Promise<Object>} URL actualizada
   */
  async updateUrlStatus(caseId, urlId, { status, notes }) {
    try {
      const response = await apiClient.patch(
        `/admin/cases/${caseId}/urls/${urlId}`,
        { status, notes }
      )
      return {
        success: true,
        data: response.data.data,
        message: response.data.message
      }
    } catch (error) {
      console.error('Update URL status error:', error)
      throw new AdminServiceError(
        error.response?.data?.error || 'Error al actualizar estado de la URL',
        error.response?.status
      )
    }
  }

  /**
   * Revisar documentación de un caso (aprobar/rechazar)
   * Endpoint: PATCH /api/admin/cases/:id/documentation
   * Sprint 4.5: Panel Admin - Validación de Documentación
   * @param {string} caseId - ID del caso
   * @param {Object} data - { action: 'approve' | 'reject', feedback?: string }
   * @returns {Promise<Object>} Resultado de la revisión
   */
  async reviewDocumentation(caseId, data) {
    try {
      const response = await apiClient.patch(
        `/admin/cases/${caseId}/documentation`,
        data
      )
      return {
        success: true,
        data: response.data.data,
        message: response.data.message
      }
    } catch (error) {
      console.error('Review documentation error:', error)
      throw new AdminServiceError(
        error.response?.data?.error || 'Error al revisar documentación',
        error.response?.status
      )
    }
  }

  /**
   * Obtener detalles de un caso por ID
   * Endpoint: GET /api/admin/cases/:id
   * Sprint 4.5: Panel Admin - Validación de Documentación
   * @param {string} caseId - ID del caso
   * @returns {Promise<Object>} Detalles del caso
   */
  async getCaseById(caseId) {
    try {
      const response = await apiClient.get(`/admin/cases/${caseId}`)
      return {
        success: true,
        data: response.data.data
      }
    } catch (error) {
      console.error('Get case by ID error:', error)
      throw new AdminServiceError(
        error.response?.data?.error || 'Error al obtener detalles del caso',
        error.response?.status
      )
    }
  }

  /**
   * Get payment statistics (admin only)
   * Sprint 03: Frontend Servicios - Pagos
   * Endpoint: GET /api/admin/payments/stats
   * @returns {Promise<Object>} Payment stats
   */
  async getPaymentStats() {
    try {
      const response = await apiClient.get('/admin/payments/stats')

      return {
        success: true,
        stats: response.data.data.stats
      }
    } catch (error) {
      console.error('Error fetching payment stats:', error)
      throw new AdminServiceError(
        error.response?.data?.error || 'Error al obtener las estadísticas de pagos',
        error.response?.status
      )
    }
  }

  /**
   * Validar documentación de un caso (alias de reviewDocumentation)
   * Sprint 4.6: Servicios Frontend para Documentación
   * @param {string} caseId - ID del caso
   * @param {string} action - 'approve' | 'reject'
   * @param {string|null} feedback - Feedback opcional para rechazo
   * @returns {Promise<Object>} Resultado de la validación
   */
  async validateDocumentation(caseId, action, feedback = null) {
    return this.reviewDocumentation(caseId, { action, feedback })
  }

  /**
   * Solicitar documento adicional al propietario del caso
   * Sprint Documentación - Fase 2: Additional Documents
   * Endpoint: POST /api/admin/cases/:id/additional-documents
   * @param {string} caseId - ID del caso
   * @param {Object} params - { name: string, reason: string }
   * @returns {Promise<Object>} { success, data, message }
   */
  async requestAdditionalDocument(caseId, { name, reason }) {
    try {
      const response = await apiClient.post(
        `/admin/cases/${caseId}/additional-documents`,
        { name, reason }
      )
      return {
        success: true,
        data: response.data.data,
        message: response.data.message
      }
    } catch (error) {
      console.error('Request additional document error:', error)
      throw new AdminServiceError(
        error.response?.data?.error || 'Error al solicitar documento adicional',
        error.response?.status
      )
    }
  }

  /**
   * Aprobar o rechazar un documento adicional
   * Endpoint: PATCH /api/admin/cases/:id/additional-documents/:docId
   * @param {string} caseId - ID del caso
   * @param {string} docId - ID del subdocumento adicional
   * @param {Object} params - { action: 'approve' | 'reject', rejectionReason?: string }
   * @returns {Promise<Object>} { success, data, message }
   */
  async reviewAdditionalDocument(caseId, docId, { action, rejectionReason }) {
    try {
      const payload = { action }
      if (rejectionReason) payload.rejectionReason = rejectionReason
      const response = await apiClient.patch(
        `/admin/cases/${caseId}/additional-documents/${docId}`,
        payload
      )
      return {
        success: true,
        data: response.data.data,
        message: response.data.message
      }
    } catch (error) {
      console.error('Review additional document error:', error)
      throw new AdminServiceError(
        error.response?.data?.error || 'Error al procesar documento adicional',
        error.response?.status
      )
    }
  }

  /**
   * Enviar recordatorio para un documento adicional pendiente
   * Endpoint: PATCH /api/admin/cases/:id/additional-documents/:docId
   * @param {string} caseId - ID del caso
   * @param {string} docId - ID del subdocumento adicional
   * @returns {Promise<Object>} { success, data, message }
   */
  async remindAdditionalDocument(caseId, docId) {
    try {
      const response = await apiClient.patch(
        `/admin/cases/${caseId}/additional-documents/${docId}`,
        { action: 'remind' }
      )
      return {
        success: true,
        data: response.data.data,
        message: response.data.message
      }
    } catch (error) {
      if (error.response?.status === 429) {
        const err = new AdminServiceError(
          error.response.data.error || 'Debe esperar para enviar otro recordatorio',
          429
        )
        err.metadata = error.response.data.metadata
        throw err
      }
      throw new AdminServiceError(
        error.response?.data?.error || 'Error al enviar recordatorio',
        error.response?.status
      )
    }
  }
}

export const adminService = new AdminService()
export default adminService